import { Switch, Route, Redirect, useLocation, useSearch } from "wouter";
import { useEffect, useMemo } from "react";
import { ProtectedRoute } from "@/components/protected-route";
import { InternalRoute } from "@/components/internal-route";
import { queryClient } from "@/lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { useUser } from "@clerk/clerk-react";
import ErrorBoundary from "@/components/ErrorBoundary";

import { useSessionLoginDetection } from "@/hooks/use-login-notification";
import { DebugLogger } from "@/components/debug-logger";
import { useOrgRouteProtection } from "@/hooks/use-org-route-protection";
import { SmsConsentDialog } from "@/components/SmsConsentDialog";
import Welcome from "@/pages/welcome";
import Auth from "@/pages/auth";
import Setup from "@/pages/setup";
import Customers from "@/pages/customers";
import Quotes from "@/pages/quotes";
import NewQuote from "@/pages/new-quote";
import QuoteTemplates from "@/pages/admin/quote-templates";
import Orders from "@/pages/orders";
import { NewOrderFlow } from "@/components/orders/NewOrderFlow";
import useTemplateFromUrl from "@/hooks/use-template-from-url";
// import Jobs from "@/pages/jobs";
import Admin from "./pages/admin";
import Services from "@/pages/admin/services";
import CustomParts from "@/pages/admin/custom-parts";
import CompanyProfile from "@/pages/admin/company-profile";
import OrderTemplates from "@/pages/admin/order-templates";
import EditOrderTemplates from "@/pages/admin/edit-order-templates";
import QuoteApproval from "@/pages/quote-approval";
import EditOrderPage from "@/pages/edit-order";
import List from "@/pages/list";
import NotFound from "@/pages/not-found";
import PcOrders from "@/pages/pc/orders";
import PcNewOrder from "@/pages/pc/new-order";
import PcContractors from "@/pages/pc/contractors";
import {
  PcOrderScopeProvider,
  ContractorScopedCache,
} from "@/components/pc/PcOrderScopeProvider";
import Invite from "@/pages/pc-onboarding/invite";
import Processing from "@/pages/pc-onboarding/processing";
import OnboardingWelcome from "@/pages/pc-onboarding/welcome";
import ContractorOnboardingWelcome from "@/pages/c-onboarding/welcome";
import ContractorOnboardingInvite from "@/pages/c-onboarding/invite";
import ContractorOnboardingLanding from "@/pages/c-onboarding/landing";
import InternalProducts from "@/pages/internal/products";
import InternalContractors from "@/pages/internal/contractors";
import SmsNotifications from "@/pages/internal/sms-notifications";
import SmsNotificationsConfirm from "@/pages/internal/sms-notifications-confirm";
import "./lib/sentry"; // initialize sentry

function NewOrderPageWrapper() {
  const [, setLocation] = useLocation();
  const { templateId, clearTemplateParam } = useTemplateFromUrl();

  const search = useSearch();
  const productId = useMemo(() => {
    const urlParams = new URLSearchParams(search);
    return urlParams.get("product");
  }, [search]);

  return (
    <NewOrderFlow
      onBack={() => setLocation("/orders")}
      templateId={templateId || undefined}
      productId={productId || undefined}
      clearTemplateParam={clearTemplateParam}
    />
  );
}

function Router() {
  const [location, setLocation] = useLocation();
  const { isSignedIn, isLoaded, user } = useUser();

  useSessionLoginDetection();
  useOrgRouteProtection();

  // Identify user in FullStory when authenticated
  useEffect(() => {
    if (isLoaded && isSignedIn && user && (window as any).FS) {
      try {
        (window as any).FS.identify(user.id, {
          displayName:
            user.fullName ||
            `${user.firstName || ""} ${user.lastName || ""}`.trim(),
          email: user.emailAddresses?.[0]?.emailAddress,
          firstName: user.firstName,
          lastName: user.lastName,
        });
      } catch (error) {
        console.warn("Failed to identify user in FullStory:", error);
      }
    }
  }, [isLoaded, isSignedIn, user]);

  // Check session and redirect if needed
  useEffect(() => {
    if (isLoaded && isSignedIn && location === "/") {
      setLocation("/orders/new");
    }
  }, [isLoaded, isSignedIn, location, setLocation]);

  return (
    <PcOrderScopeProvider>
      <Switch>
        <Route path="/login" component={Auth} />
        <Route path="/" component={Welcome} />
        <Route path="/setup" component={Setup} />
        <Route path="/setup/verify-phone-number" component={Setup} />

        {/* Public Quote Approval Route */}
        <Route path="/quote-approval/:quote_id" component={QuoteApproval} />

        {/* Protected Routes */}
        <Route path="/admin">
          <ProtectedRoute>
            <Admin />
          </ProtectedRoute>
        </Route>
        <Route path="/admin/quote-templates">
          <ProtectedRoute>
            <QuoteTemplates />
          </ProtectedRoute>
        </Route>
        <Route path="/admin/services">
          <ProtectedRoute>
            <Services />
          </ProtectedRoute>
        </Route>
        <Route path="/admin/custom-parts">
          <ProtectedRoute>
            <CustomParts />
          </ProtectedRoute>
        </Route>
        <Route path="/admin/company-profile">
          <ProtectedRoute>
            <CompanyProfile />
          </ProtectedRoute>
        </Route>
        <Route path="/admin/order-templates">
          <ProtectedRoute>
            <OrderTemplates />
          </ProtectedRoute>
        </Route>
        <Route path="/admin/order-templates/edit">
          <ProtectedRoute>
            <EditOrderTemplates />
          </ProtectedRoute>
        </Route>
        <Route path="/internal/products">
          <InternalRoute>
            <InternalProducts />
          </InternalRoute>
        </Route>
        <Route path="/internal/contractors">
          <InternalRoute>
            <InternalContractors />
          </InternalRoute>
        </Route>
        <Route path="/internal/sms-notifications/confirm">
          <InternalRoute>
            <SmsNotificationsConfirm />
          </InternalRoute>
        </Route>
        <Route path="/internal/sms-notifications">
          <InternalRoute>
            <SmsNotifications />
          </InternalRoute>
        </Route>
        <Route path="/home">
          <Redirect to="/orders/new" />
        </Route>
        <Route path="/customers">
          <ProtectedRoute>
            <Customers />
          </ProtectedRoute>
        </Route>
        <Route path="/quotes">
          <ProtectedRoute>
            <Quotes />
          </ProtectedRoute>
        </Route>
        <Route path="/quotes/new">
          <ProtectedRoute>
            <NewQuote />
          </ProtectedRoute>
        </Route>
        <Route path="/orders">
          <ProtectedRoute>
            <Orders />
          </ProtectedRoute>
        </Route>
        <Route path="/orders/new">
          <ProtectedRoute>
            <NewOrderPageWrapper />
          </ProtectedRoute>
        </Route>
        {/* Also used by PC staff acting for a contractor (via the list flow),
            so their per-contractor cache applies here */}
        <Route path="/orders/edit/:id">
          <ProtectedRoute>
            <ContractorScopedCache>
              <EditOrderPage />
            </ContractorScopedCache>
          </ProtectedRoute>
        </Route>
        <Route path="/list">
          <ProtectedRoute>
            <ContractorScopedCache>
              <List />
            </ContractorScopedCache>
          </ProtectedRoute>
        </Route>
        <Route path="/pc/orders">
          <ProtectedRoute>
            <PcOrders />
          </ProtectedRoute>
        </Route>
        <Route path="/pc/orders/new">
          <ProtectedRoute>
            <ContractorScopedCache>
              <PcNewOrder />
            </ContractorScopedCache>
          </ProtectedRoute>
        </Route>
        <Route path="/pc/contractors">
          <ProtectedRoute>
            <PcContractors />
          </ProtectedRoute>
        </Route>
        <Route path="/pc-onboarding/welcome">
          <ProtectedRoute>
            <OnboardingWelcome />
          </ProtectedRoute>
        </Route>
        <Route path="/pc-onboarding/invite">
          <ProtectedRoute>
            <Invite />
          </ProtectedRoute>
        </Route>
        <Route path="/pc-onboarding/processing">
          <ProtectedRoute>
            <Processing />
          </ProtectedRoute>
        </Route>
        <Route path="/c-onboarding/welcome">
          <ProtectedRoute>
            <ContractorOnboardingWelcome />
          </ProtectedRoute>
        </Route>
        <Route path="/c-onboarding/invite">
          <ProtectedRoute>
            <ContractorOnboardingInvite />
          </ProtectedRoute>
        </Route>
        <Route path="/c-onboarding/landing">
          <ProtectedRoute>
            <ContractorOnboardingLanding />
          </ProtectedRoute>
        </Route>
        {/* <Route path="/jobs">
          <ProtectedRoute>
            <Jobs />
          </ProtectedRoute>
        </Route> */}

        <Route component={NotFound} />
      </Switch>

      {isSignedIn && <SmsConsentDialog />}
    </PcOrderScopeProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        {import.meta.env.DEV && <DebugLogger />}
        <Router />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
